import { styled } from 'styled-system/jsx'

const Backdrop = styled('div', {
  base: {
    position: 'fixed',
    inset: '0',
    backgroundColor: 'rgba(26, 19, 48, 0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    zIndex: '1000',
  },
})

const Card = styled('div', {
  base: {
    backgroundColor: '#FFFFFF',
    borderRadius: 'lg',
    width: '100%',
    maxWidth: '480px',
    maxHeight: '86vh',
    overflowY: 'auto',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    boxShadow: '0 30px 80px -30px rgba(128, 84, 255, 0.5)',
  },
})

const Title = styled('h2', {
  base: {
    fontFamily: 'display',
    fontSize: '20px',
    fontWeight: '800',
    color: 'quickpass.ink',
    margin: '0',
    letterSpacing: '-0.4px',
  },
})

const Subtitle = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
    margin: '0',
    lineHeight: '1.45',
  },
})

const GoalList = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
})

const GoalCard = styled('button', {
  base: {
    textAlign: 'left',
    cursor: 'pointer',
    backgroundColor: '#FFFFFF',
    border: '1px solid',
    borderColor: '#EAE6F5',
    borderRadius: 'md',
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '13px',
    transition: 'border-color 150ms, transform 150ms',
    _hover: {
      borderColor: 'quickpass.primary',
      transform: 'translateY(-1px)',
    },
  },
})

const GoalIcon = styled('span', {
  base: {
    fontSize: '24px',
    lineHeight: '1',
    flexShrink: '0',
  },
})

const GoalText = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    flex: '1',
    minWidth: '0',
  },
})

const GoalName = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '15px',
    fontWeight: '700',
    color: 'quickpass.ink',
  },
})

const GoalMeta = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
  },
})

const ReadyPill = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '11px',
    fontWeight: '700',
    paddingX: '8px',
    paddingY: '3px',
    borderRadius: 'pill',
    backgroundColor: '#F1ECFF',
    color: 'quickpass.primary',
    flexShrink: '0',
    whiteSpace: 'nowrap',
  },
})

const ChecklistList = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
  },
})

const ChecklistRow = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    padding: '12px',
    borderRadius: 'md',
    border: '1px solid',
    borderColor: '#EFEBFA',
  },
})

const ChecklistTop = styled('div', {
  base: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
  },
})

const CheckMark = styled('span', {
  base: {
    fontSize: '15px',
    lineHeight: '1',
    flexShrink: '0',
  },
  variants: {
    satisfied: {
      true: { color: '#10663A' },
      false: { color: 'quickpass.inkSoft' },
    },
  },
})

const CheckName = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '700',
    color: 'quickpass.ink',
    flex: '1',
  },
})

const BlockerTag = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '10px',
    fontWeight: '700',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: '#9A2222',
  },
})

const CheckValue = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: '#10663A',
    paddingLeft: '24px',
  },
})

const CheckInput = styled('input', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    border: '1px solid',
    borderColor: '#E0DAF2',
    borderRadius: 'sm',
    padding: '8px 10px',
    marginLeft: '24px',
    outline: 'none',
    _focus: { borderColor: 'quickpass.primary' },
  },
})

const ProofCallout = styled('div', {
  base: {
    backgroundColor: '#FAF7FF',
    border: '1px solid',
    borderColor: '#E6DEFB',
    borderRadius: 'md',
    padding: '13px 15px',
    fontFamily: 'body',
    fontSize: '13px',
    color: 'quickpass.inkSoft',
    lineHeight: '1.5',
  },
})

const MessageTextarea = styled('textarea', {
  base: {
    width: '100%',
    minHeight: '120px',
    resize: 'vertical',
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.ink',
    border: '1px solid',
    borderColor: '#E0DAF2',
    borderRadius: 'md',
    padding: '12px',
    outline: 'none',
    lineHeight: '1.5',
    _focus: { borderColor: 'quickpass.primary' },
  },
})

const CharCount = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    fontWeight: '600',
    alignSelf: 'flex-end',
  },
  variants: {
    met: {
      true: { color: '#10663A' },
      false: { color: 'quickpass.inkSoft' },
    },
  },
})

const ShareToggle = styled('button', {
  base: {
    cursor: 'pointer',
    textAlign: 'left',
    display: 'flex',
    alignItems: 'center',
    gap: '11px',
    padding: '11px 13px',
    borderRadius: 'md',
    border: '1px solid',
    borderColor: '#EAE6F5',
    backgroundColor: '#FFFFFF',
    transition: 'border-color 150ms, background-color 150ms',
  },
  variants: {
    on: {
      true: { borderColor: 'quickpass.primary', backgroundColor: '#F6F2FF' },
      false: {},
    },
  },
})

const ShareToggleMark = styled('span', {
  base: {
    fontSize: '16px',
    lineHeight: '1',
    flexShrink: '0',
  },
})

const ShareToggleText = styled('div', {
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
})

const ShareToggleTitle = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    fontWeight: '700',
    color: 'quickpass.ink',
  },
})

const ShareToggleSub = styled('span', {
  base: {
    fontFamily: 'body',
    fontSize: '12px',
    color: 'quickpass.inkSoft',
  },
})

const Footer = styled('div', {
  base: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '10px',
    marginTop: '4px',
  },
})

const ErrorText = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '13px',
    color: '#9A2222',
    margin: '0',
  },
})

const LoadingText = styled('p', {
  base: {
    fontFamily: 'body',
    fontSize: '14px',
    color: 'quickpass.inkSoft',
    margin: '0',
    textAlign: 'center',
    paddingY: '24px',
  },
})

export {
  Backdrop,
  Card,
  Title,
  Subtitle,
  GoalList,
  GoalCard,
  GoalIcon,
  GoalText,
  GoalName,
  GoalMeta,
  ReadyPill,
  ProofCallout,
  MessageTextarea,
  CharCount,
  ShareToggle,
  ShareToggleMark,
  ShareToggleText,
  ShareToggleTitle,
  ShareToggleSub,
  ChecklistList,
  ChecklistRow,
  ChecklistTop,
  CheckMark,
  CheckName,
  BlockerTag,
  CheckValue,
  CheckInput,
  Footer,
  ErrorText,
  LoadingText,
}
